import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeftRight,
  CreditCard,
  Gauge,
  LifeBuoy,
  type LucideIcon,
  Megaphone,
  ShieldAlert,
  UserRoundPlus,
  UsersRound,
} from "lucide-react";
import { type FormEvent, type ReactNode, useState } from "react";
import { Link, Navigate, NavLink, useLocation } from "react-router-dom";
import { APIError, api, jsonBody, queryString } from "../../api/client";
import type { ListResponse } from "../../api/types";
import { useAuth } from "../../auth/AuthProvider";
import { useSensitiveAction } from "../../auth/SensitiveActionGate";
import { formatDate, formatMoney, titleCase } from "../../lib/format";
import {
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorPanel,
  Field,
  FormActions,
  Notice,
  PageHeader,
  StatusPill,
} from "../../ui/components";
import { ListPageSkeleton, SkeletonTable } from "../../ui/skeletons";
import { ControlPlane } from "./ControlPlane";
import { OwnershipQueue } from "./OwnershipQueue";
import {
  type OperatorSection,
  type OperatorSectionId,
  operatorRoleCanMutate,
  operatorSectionFromPath,
  operatorSectionPath,
  operatorSectionsForRole,
} from "./operatorNavigation";

interface TenantSummary {
  id: string;
  slug: string;
  legalName: string;
  displayName: string;
  status: string;
  timezone: string;
  locale: string;
  currency: string;
  dataRegion: string;
  onboardingState: string;
  subscriptionStatus: string;
  subscriptionPriceMinor?: number;
  connectedAccountState: string;
  connectedChargesEnabled: boolean;
  connectedPayoutsEnabled: boolean;
  membershipCount: number;
  venueCount: number;
  bookingCount: number;
  openBalanceMinor: number;
  createdAt: string;
  subscriptionPeriodEnd?: string;
}

interface ProvisionResult {
  tenant: TenantSummary;
  ownerEmail: string;
  planCode: string;
  billingInterval: "monthly" | "annual";
  introductoryTrialDays: number;
  invitationUrl?: string;
  expiresAt?: string;
  replay: boolean;
  emailDeliveryQueued: boolean;
}

interface OwnerInvitationResult {
  tenantId: string;
  ownerEmail: string;
  invitationUrl?: string;
  expiresAt: string;
  emailDeliveryQueued: boolean;
}

interface Adjustment {
  id: string;
  tenantId: string;
  type: "credit" | "refund" | "complimentary_months";
  amountMinor?: number;
  months?: number;
  reason: string;
  status: "pending" | "applied" | "failed" | "recorded";
  provider?: string;
  environment?: string;
  providerId?: string;
  lastError?: string;
  attempts: number;
  emailDeliveryQueued: boolean;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface SupportGrant {
  id: string;
  tenantId: string;
  operatorUserId: string;
  authorisedByUserId: string;
  reason: string;
  scopes: string[];
  startsAt: string;
  expiresAt: string;
  revokedAt?: string;
}

interface SupportSnapshot {
  grant: SupportGrant;
  tenant: TenantSummary;
  configuration?: Record<string, unknown>;
  bookingStatus?: Record<string, number>;
  invoiceStatus?: Record<string, number>;
  recentAuditCount?: number;
}

const sectionIcons: Record<OperatorSectionId, LucideIcon> = {
  overview: Gauge,
  customers: UsersRound,
  invitations: UserRoundPlus,
  billing: CreditCard,
  support: LifeBuoy,
  reliability: ShieldAlert,
  notices: Megaphone,
  ownership: ArrowLeftRight,
};

function StatusBreakdown({
  label,
  values,
}: {
  label: string;
  values?: Record<string, number> | undefined;
}) {
  const entries = Object.entries(values ?? {}).sort(([left], [right]) => left.localeCompare(right));
  return (
    <div>
      <strong>{label}</strong>
      {entries.length ? (
        <dl className="definition-list">
          {entries.flatMap(([status, count]) => [
            <dt key={`${status}-label`}>{titleCase(status)}</dt>,
            <dd key={`${status}-value`}>{count}</dd>,
          ])}
        </dl>
      ) : (
        <p>
          <small>Not included in the active support grant.</small>
        </p>
      )}
    </div>
  );
}

function CustomerSelector({
  tenants,
  selected,
  onSelect,
}: {
  tenants: TenantSummary[];
  selected: TenantSummary | null;
  onSelect(tenantId: string): void;
}) {
  return (
    <Card>
      <CardHeader
        title="Choose a customer"
        description="The selected account is shared by the controls in this area."
      />
      {tenants.length && selected ? (
        <Field label="Customer account">
          <select value={selected.id} onChange={(event) => onSelect(event.target.value)}>
            {tenants.map((tenant) => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.displayName}
              </option>
            ))}
          </select>
        </Field>
      ) : (
        <EmptyState
          title="No customer accounts"
          description="Create the first invitation after commercial approval."
        />
      )}
    </Card>
  );
}

function isMFARequired(error: unknown): boolean {
  return error instanceof APIError && error.code === "mfa_required";
}

interface OperatorFrameProps {
  sections: readonly OperatorSection[];
  activeSection: OperatorSection | undefined;
  requestedSection: OperatorSection | undefined;
  onNavigate: () => void;
  children: ReactNode;
}

/**
 * The page header, the area navigation and the area heading, drawn whatever the area holds: the
 * operator can always see where they are and move between areas, while what an area shows is the
 * caller's decision. An address outside the operator's fixed role, or outside the map, is refused
 * here so that every branch of the page refuses it the same way.
 */
function OperatorFrame({
  sections,
  activeSection,
  requestedSection,
  onNavigate,
  children,
}: OperatorFrameProps) {
  return (
    <>
      <PageHeader
        title="Platform admin"
        description="Manage the platform through focused areas with fixed operator responsibilities and audited controls."
      />
      <div className="operator-workspace">
        <nav className="operator-section-nav" aria-label="Platform admin areas">
          {sections.map((section) => {
            const Icon = sectionIcons[section.id];
            return (
              <NavLink
                key={section.id}
                to={operatorSectionPath(section.id)}
                end
                onClick={onNavigate}
              >
                <Icon size={18} aria-hidden="true" />
                <span>
                  <strong>{section.label}</strong>
                  <small>{section.description}</small>
                </span>
              </NavLink>
            );
          })}
        </nav>
        <section
          className="operator-section-content"
          aria-labelledby={activeSection ? `operator-${activeSection.id}-heading` : undefined}
        >
          {activeSection ? (
            <>
              <header className="operator-section-heading">
                <span className="eyebrow">Platform workspace</span>
                <h2 id={`operator-${activeSection.id}-heading`}>{activeSection.label}</h2>
                <p>{activeSection.description}</p>
              </header>
              {children}
            </>
          ) : (
            <>
              <header className="operator-section-heading">
                <span className="eyebrow">Platform workspace</span>
                <h2>{requestedSection ? "Access restricted" : "Area not found"}</h2>
              </header>
              <Notice tone="danger">
                {requestedSection
                  ? "Your fixed platform role does not allow access to this admin area."
                  : "This platform admin address does not match an available area."}
              </Notice>
            </>
          )}
        </section>
      </div>
    </>
  );
}

export function OperatorPage() {
  const auth = useAuth();
  const location = useLocation();
  const platformRole = auth.session?.user.platformRole ?? "none";
  const requestedSection = operatorSectionFromPath(location.pathname);
  const visibleSections = operatorSectionsForRole(platformRole);
  const activeSection = requestedSection
    ? visibleSections.find((section) => section.id === requestedSection.id)
    : undefined;
  // No setup title: an account without two-step verification never reaches the gate's own view,
  // because the whole workspace waits for it below.
  const sensitive = useSensitiveAction({
    lockedTitle: "Confirm your identity to manage customer accounts",
    lockedDescription:
      "These changes can affect customer access, billing, support, or ownership. Enter a fresh code before continuing.",
  });
  const securityPath = auth.session?.selectedMembership ? "/settings/security" : "/security";
  const tenants = useQuery({
    queryKey: ["operator", "tenants"],
    queryFn: () =>
      api<ListResponse<TenantSummary>>(`/api/v1/operator/tenants${queryString({ limit: 100 })}`),
    // The server refuses every platform route, reads included, to a session that has not proved a
    // second factor, so nothing platform-wide is asked for until the account has an authenticator
    // app; otherwise the refusal would be the first thing a new operator saw.
    enabled: platformRole !== "none" && sensitive.mfaEnabled === true,
  });
  const items = tenants.data?.items ?? [];
  const [selected, setSelected] = useState<TenantSummary | null>(null);
  const selectedTenant =
    selected && items.some((tenant) => tenant.id === selected.id) ? selected : (items[0] ?? null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();
  const [provisioned, setProvisioned] = useState<ProvisionResult | null>(null);
  const [reissued, setReissued] = useState<OwnerInvitationResult | null>(null);
  const [snapshot, setSnapshot] = useState<SupportSnapshot | null>(null);
  const [adjustmentType, setAdjustmentType] = useState<Adjustment["type"]>("complimentary_months");
  const [adjustmentResult, setAdjustmentResult] = useState<Adjustment | null>(null);
  const canManageTenants = platformRole === "platform_owner";
  const canManageBilling = platformRole === "platform_owner" || platformRole === "billing_operator";
  const canSupport =
    platformRole === "platform_owner" ||
    platformRole === "support_operator" ||
    platformRole === "security_operator";
  const supportNoteCategories =
    platformRole === "platform_owner"
      ? ["support", "billing", "security", "onboarding", "general"]
      : platformRole === "security_operator"
        ? ["support", "security", "onboarding", "general"]
        : platformRole === "support_operator"
          ? ["support", "onboarding", "general"]
          : [];
  const noteCategories =
    activeSection?.id === "support"
      ? supportNoteCategories
      : activeSection?.id === "billing" && platformRole === "billing_operator"
        ? ["billing"]
        : [];
  const showSensitiveGate = activeSection
    ? operatorRoleCanMutate(platformRole, activeSection.id)
    : false;
  const adjustments = useQuery({
    queryKey: ["operator", "adjustments", selectedTenant?.id],
    queryFn: () =>
      api<{ items: Adjustment[] }>(`/api/v1/operator/tenants/${selectedTenant?.id}/adjustments`),
    enabled: Boolean(
      activeSection?.id === "billing" && selectedTenant && canManageBilling && sensitive.unlocked,
    ),
  });

  function selectTenant(tenantId: string) {
    setSelected(items.find((tenant) => tenant.id === tenantId) ?? null);
    setSnapshot(null);
    setError(undefined);
  }

  async function provision(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setWorking(true);
    setError(undefined);
    setProvisioned(null);
    try {
      const result = await api<ProvisionResult>("/api/v1/operator/tenants", {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: jsonBody({
          slug: String(form.get("slug")),
          legalName: String(form.get("legalName")),
          displayName: String(form.get("displayName")),
          timezone: String(form.get("timezone")),
          locale: String(form.get("locale")),
          dataRegion: String(form.get("dataRegion")),
          ownerName: String(form.get("ownerName")),
          ownerEmail: String(form.get("ownerEmail")),
          planCode: String(form.get("planCode")),
          billingInterval: String(form.get("billingInterval")),
          introductoryTrialDays: Number(form.get("introductoryTrialDays") || 0),
        }),
      });
      setProvisioned(result);
      formElement.reset();
      await tenants.refetch();
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function reissueOwnerInvitation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedTenant) return;
    const form = new FormData(event.currentTarget);
    setWorking(true);
    setError(undefined);
    setReissued(null);
    try {
      setReissued(
        await api<OwnerInvitationResult>(
          `/api/v1/operator/tenants/${selectedTenant.id}/owner-invitation`,
          { method: "POST", body: jsonBody({ reason: String(form.get("reason")) }) },
        ),
      );
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function updateStatus(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedTenant) return;
    const form = new FormData(event.currentTarget);
    setWorking(true);
    setError(undefined);
    try {
      const item = await api<TenantSummary>(
        `/api/v1/operator/tenants/${selectedTenant.id}/status`,
        {
          method: "PUT",
          body: jsonBody({
            status: String(form.get("status")),
            reason: String(form.get("reason")),
          }),
        },
      );
      setSelected(item);
      await tenants.refetch();
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function updateOnboarding(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedTenant) return;
    const form = new FormData(event.currentTarget);
    setWorking(true);
    setError(undefined);
    try {
      const item = await api<TenantSummary>(
        `/api/v1/operator/tenants/${selectedTenant.id}/onboarding`,
        {
          method: "PUT",
          body: jsonBody({ state: String(form.get("state")), reason: String(form.get("reason")) }),
        },
      );
      setSelected(item);
      await tenants.refetch();
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function addAdjustment(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedTenant) return;
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const raw = Number(form.get("value"));
    setWorking(true);
    setError(undefined);
    setAdjustmentResult(null);
    try {
      const item = await api<Adjustment>(
        `/api/v1/operator/tenants/${selectedTenant.id}/adjustments`,
        {
          method: "POST",
          body: jsonBody({
            type: adjustmentType,
            amountMinor: adjustmentType === "complimentary_months" ? null : Math.round(raw * 100),
            months: adjustmentType === "complimentary_months" ? Math.round(raw) : null,
            reason: String(form.get("reason")),
          }),
        },
      );
      setAdjustmentResult(item);
      formElement.reset();
      await adjustments.refetch();
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function retryAdjustment(item: Adjustment) {
    if (!selectedTenant) return;
    setWorking(true);
    setError(undefined);
    setAdjustmentResult(null);
    try {
      const result = await api<Adjustment>(
        `/api/v1/operator/tenants/${selectedTenant.id}/adjustments/${item.id}/retry`,
        { method: "POST" },
      );
      setAdjustmentResult(result);
      await adjustments.refetch();
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function addNote(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedTenant) return;
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setWorking(true);
    setError(undefined);
    try {
      await api(`/api/v1/operator/tenants/${selectedTenant.id}/notes`, {
        method: "POST",
        body: jsonBody({ category: String(form.get("category")), body: String(form.get("body")) }),
      });
      formElement.reset();
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function loadSnapshot() {
    if (!selectedTenant) return;
    setWorking(true);
    setError(undefined);
    setSnapshot(null);
    try {
      setSnapshot(
        await api<SupportSnapshot>(
          `/api/v1/operator/tenants/${selectedTenant.id}/support-snapshot`,
        ),
      );
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  function leaveSection() {
    setError(undefined);
    setSnapshot(null);
  }

  if (platformRole === "none") {
    return (
      <Notice tone="danger">
        This route is restricted to Pixel Apocalypse platform operators.
      </Notice>
    );
  }
  if (location.pathname === "/operator" || location.pathname === "/operator/") {
    return <Navigate to={operatorSectionPath("overview")} replace />;
  }
  const frame = {
    sections: visibleSections,
    activeSection,
    requestedSection,
    onNavigate: leaveSection,
  };
  if (sensitive.mfaEnabled !== true) {
    // The status request is the one call this branch makes. While it loads, or if it fails, the
    // gate's own view says so; once it answers that nothing is set up, the areas stay visible and
    // empty, with the one thing that opens them named.
    return (
      <OperatorFrame {...frame}>
        {sensitive.mfaEnabled === false ? (
          <Notice tone="warning" title="Platform administration needs two-step verification">
            <span>
              Every platform area, including the read-only ones, waits until your account has an
              authenticator app, because a platform role reaches every customer's data.{" "}
              <Link to={securityPath}>Set up two-step verification</Link>, then come back here.
            </span>
          </Notice>
        ) : (
          sensitive.view
        )}
      </OperatorFrame>
    );
  }
  if (tenants.isLoading)
    return (
      <ListPageSkeleton
        title="Platform admin"
        description="Manage the platform through focused areas with fixed operator responsibilities and audited controls."
        columnCount={8}
        rowCount={6}
      />
    );
  if (tenants.error) {
    if (!isMFARequired(tenants.error)) return <ErrorPanel error={tenants.error} />;
    // Two-step verification is set up, but this session was signed in with a password alone: it
    // was open before the app was enrolled from another browser. Only a sign-in that proved the
    // second factor opens the platform, so that is what is asked for.
    return (
      <OperatorFrame {...frame}>
        <Notice tone="warning" title="Sign in again with a code">
          <span>
            Two-step verification is set up on your account, but this session was signed in with a
            password alone. Sign out, then sign in again and enter a code from your authenticator
            app to open platform administration.
          </span>
        </Notice>
      </OperatorFrame>
    );
  }

  return (
    <OperatorFrame {...frame}>
      {activeSection ? (
        <>
          {showSensitiveGate ? sensitive.view : null}
          {error ? <ErrorPanel error={error} /> : null}
          {activeSection.id === "overview" ? (
            <>
              <ControlPlane
                tenants={items}
                role={platformRole}
                sensitive={sensitive}
                section="overview"
                tenantId={selectedTenant?.id ?? ""}
                onTenantChange={selectTenant}
              />
              <Notice tone="warning" title="Separate trust boundary">
                Platform access never silently becomes tenant access. Sensitive actions require a
                recent identity check, fixed platform permission, an explicit reason, and an audit
                trail.
              </Notice>
            </>
          ) : null}
          {activeSection.id === "invitations" ? (
            <>
              {provisioned?.invitationUrl ? (
                <Notice
                  tone={provisioned.emailDeliveryQueued ? "success" : "warning"}
                  title={provisioned.replay ? "Tenant already provisioned" : "Tenant provisioned"}
                >
                  {provisioned.emailDeliveryQueued ? (
                    <span>
                      The {titleCase(provisioned.planCode)} package invitation for{" "}
                      {provisioned.ownerEmail} is queued for email delivery.
                    </span>
                  ) : (
                    <span>
                      Email delivery is not configured, so send this{" "}
                      {titleCase(provisioned.planCode)} package invitation to{" "}
                      {provisioned.ownerEmail} securely:{" "}
                      <a href={provisioned.invitationUrl}>{provisioned.invitationUrl}</a>
                    </span>
                  )}
                </Notice>
              ) : null}
              <Card>
                <CardHeader
                  title="Create customer invitation"
                  description="Creates the isolated tenant boundary and binds a package, billing interval, and optional introductory period to a single-use owner invitation."
                />
                <form className="form-grid" onSubmit={provision}>
                  <Field label="Slug">
                    <input name="slug" required pattern="[a-z0-9][a-z0-9-]{1,61}[a-z0-9]" />
                  </Field>
                  <Field label="Legal name">
                    <input name="legalName" required />
                  </Field>
                  <Field label="Display name">
                    <input name="displayName" required />
                  </Field>
                  <Field label="Timezone">
                    <input name="timezone" defaultValue="America/New_York" required />
                  </Field>
                  <Field label="Locale">
                    <select name="locale" defaultValue="en-US">
                      <option value="en-US">English (US)</option>
                      <option value="en-GB">English (UK)</option>
                    </select>
                  </Field>
                  <Field label="Data region">
                    <select name="dataRegion" defaultValue="ca-east">
                      <option value="ca-east">Canada East</option>
                      <option value="us-east">US East</option>
                      <option value="uk-south">UK South</option>
                      <option value="eu-west">EU West</option>
                    </select>
                  </Field>
                  <Field label="Owner name">
                    <input name="ownerName" required />
                  </Field>
                  <Field label="Owner email">
                    <input name="ownerEmail" type="email" required />
                  </Field>
                  <Field label="Package">
                    <select name="planCode" defaultValue="business">
                      <option value="starter">Starter</option>
                      <option value="business">Business</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </Field>
                  <Field label="Billing interval">
                    <select name="billingInterval" defaultValue="monthly">
                      <option value="monthly">Monthly</option>
                      <option value="annual">Annual</option>
                    </select>
                  </Field>
                  <Field
                    label="Introductory period (days)"
                    hint="Use 0 for standard billing or up to 31 days for a written founding-customer offer."
                  >
                    <input
                      name="introductoryTrialDays"
                      type="number"
                      min={0}
                      max={31}
                      defaultValue={0}
                    />
                  </Field>
                  <div className="full">
                    <FormActions
                      submitting={working}
                      disabled={!sensitive.unlocked}
                      submitLabel="Create customer invitation"
                    />
                  </div>
                </form>
              </Card>
            </>
          ) : null}
          {activeSection.id === "customers" ? (
            <>
              {reissued?.invitationUrl ? (
                <Notice
                  tone={reissued.emailDeliveryQueued ? "success" : "warning"}
                  title="Replacement owner invitation created"
                >
                  {reissued.emailDeliveryQueued ? (
                    <span>
                      The replacement invitation for {reissued.ownerEmail} is queued for delivery.
                    </span>
                  ) : (
                    <span>
                      Send this replacement link securely to {reissued.ownerEmail}:{" "}
                      <a href={reissued.invitationUrl}>{reissued.invitationUrl}</a>
                    </span>
                  )}
                </Notice>
              ) : null}
              <Card>
                <CardHeader
                  title="Customer accounts"
                  description="Platform-level account and operational summary with no tenant personal records."
                />
                {items.length ? (
                  <DataTable
                    caption="Platform customer accounts"
                    items={items}
                    getKey={(item) => item.id}
                    columns={[
                      {
                        label: "Customer",
                        render: (item) => (
                          <button
                            className="button ghost small"
                            type="button"
                            aria-pressed={selectedTenant?.id === item.id}
                            onClick={() => selectTenant(item.id)}
                          >
                            <span>
                              <strong>{item.displayName}</strong>
                              <br />
                              <small>{item.slug}</small>
                            </span>
                          </button>
                        ),
                      },
                      {
                        label: "Subscription",
                        render: (item) => <StatusPill value={item.subscriptionStatus} />,
                      },
                      {
                        label: "Connect",
                        render: (item) => <StatusPill value={item.connectedAccountState} />,
                      },
                      {
                        label: "Onboarding",
                        render: (item) => <StatusPill value={item.onboardingState} />,
                      },
                      { label: "Users", render: (item) => item.membershipCount },
                      { label: "Venues", render: (item) => item.venueCount },
                      { label: "Bookings", render: (item) => item.bookingCount },
                      { label: "Status", render: (item) => <StatusPill value={item.status} /> },
                    ]}
                  />
                ) : (
                  <EmptyState
                    title="No customer accounts"
                    description="Create the first invitation after commercial approval."
                  />
                )}
              </Card>
              {selectedTenant ? (
                <div className="content-grid">
                  <Card className={canManageTenants ? "span-5" : "span-12"}>
                    <CardHeader
                      title={selectedTenant.displayName}
                      description={selectedTenant.legalName}
                      actions={<StatusPill value={selectedTenant.status} />}
                    />
                    <dl className="definition-list">
                      <dt>Region</dt>
                      <dd>{selectedTenant.dataRegion}</dd>
                      <dt>Timezone</dt>
                      <dd>{selectedTenant.timezone}</dd>
                      <dt>Onboarding</dt>
                      <dd>{titleCase(selectedTenant.onboardingState)}</dd>
                      <dt>Subscription</dt>
                      <dd>{titleCase(selectedTenant.subscriptionStatus)}</dd>
                      <dt>Subscription price</dt>
                      <dd>
                        {selectedTenant.subscriptionPriceMinor === undefined
                          ? "Not set"
                          : formatMoney(
                              selectedTenant.subscriptionPriceMinor,
                              selectedTenant.currency,
                            )}
                      </dd>
                      <dt>Period end</dt>
                      <dd>{formatDate(selectedTenant.subscriptionPeriodEnd)}</dd>
                      <dt>Connected payments</dt>
                      <dd>{titleCase(selectedTenant.connectedAccountState)}</dd>
                      <dt>Created</dt>
                      <dd>{formatDate(selectedTenant.createdAt)}</dd>
                    </dl>
                    {canManageTenants && selectedTenant.subscriptionStatus === "incomplete" ? (
                      <form className="form-stack" onSubmit={reissueOwnerInvitation}>
                        <Field
                          label="Replacement invitation reason"
                          hint="This invalidates every previous open owner invitation for this membership."
                        >
                          <textarea name="reason" required minLength={10} />
                        </Field>
                        <FormActions
                          submitting={working}
                          disabled={!sensitive.unlocked}
                          submitLabel="Create replacement invitation"
                        />
                      </form>
                    ) : null}
                  </Card>
                  {canManageTenants ? (
                    <Card className="span-7">
                      <div className="stack">
                        <form
                          key={`status-${selectedTenant.id}-${selectedTenant.status}`}
                          className="form-stack"
                          onSubmit={updateStatus}
                        >
                          <CardHeader
                            title="Account access"
                            description="Pause access only when necessary. Closing an account is permanent."
                          />
                          <Field label="Account status">
                            <select name="status" defaultValue={selectedTenant.status}>
                              <option value="active">Active: normal access</option>
                              <option value="suspended">Paused: sign-ins are stopped</option>
                              <option value="closed">Closed: permanent</option>
                            </select>
                          </Field>
                          <Field label="Why is this changing?">
                            <textarea name="reason" required minLength={10} />
                          </Field>
                          <FormActions
                            submitting={working}
                            disabled={!sensitive.unlocked}
                            submitLabel="Save account status"
                          />
                        </form>
                        <form
                          key={`onboarding-${selectedTenant.id}-${selectedTenant.onboardingState}`}
                          className="form-stack"
                          onSubmit={updateOnboarding}
                        >
                          <CardHeader
                            title="Onboarding progress"
                            description="Keep the setup stage current so the next task is easy to see."
                          />
                          <Field label="Current stage">
                            <select name="state" defaultValue={selectedTenant.onboardingState}>
                              <option value="configuration">Workspace details</option>
                              <option value="billing">Subscription billing</option>
                              <option value="owner_security">Owner security</option>
                              <option value="venue_setup">Venues and spaces</option>
                              <option value="ready">Ready to use</option>
                            </select>
                          </Field>
                          <Field label="Update note">
                            <textarea name="reason" required minLength={10} />
                          </Field>
                          <FormActions
                            submitting={working}
                            disabled={!sensitive.unlocked}
                            submitLabel="Save onboarding stage"
                          />
                        </form>
                      </div>
                    </Card>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : null}
          {activeSection.id === "billing" ? (
            <>
              <ControlPlane
                tenants={items}
                role={platformRole}
                sensitive={sensitive}
                section="billing"
                tenantId={selectedTenant?.id ?? ""}
                onTenantChange={selectTenant}
              />
              {adjustmentResult ? (
                <Notice
                  tone={adjustmentResult.status === "applied" ? "success" : "danger"}
                  title={
                    adjustmentResult.status === "applied"
                      ? "Billing action completed"
                      : "Billing action needs attention"
                  }
                >
                  {adjustmentResult.status === "applied"
                    ? adjustmentResult.emailDeliveryQueued
                      ? "The provider accepted the action and a confirmation email is queued for the customer’s billing contact."
                      : "The provider accepted the action, but email delivery is not configured. Contact the customer’s billing contact manually."
                    : (adjustmentResult.lastError ??
                      "The billing provider did not complete this action. Retry it from the history below.")}
                </Notice>
              ) : null}
              {selectedTenant ? (
                <Card>
                  <CardHeader
                    title={`Subscription interventions for ${selectedTenant.displayName}`}
                    description="Credits, complimentary months, and latest-payment refunds remain separate from tenant booking payments."
                    actions={<StatusPill value={selectedTenant.subscriptionStatus} />}
                  />
                  <form className="form-grid" onSubmit={addAdjustment}>
                    <Field label="Action">
                      <select
                        name="type"
                        value={adjustmentType}
                        onChange={(event) =>
                          setAdjustmentType(event.target.value as Adjustment["type"])
                        }
                      >
                        <option value="complimentary_months">Grant free months</option>
                        <option value="credit">Add account credit</option>
                        <option value="refund">Refund latest payment</option>
                      </select>
                    </Field>
                    <Field
                      label={
                        adjustmentType === "complimentary_months"
                          ? "Number of months"
                          : "Amount (USD)"
                      }
                      hint={
                        adjustmentType === "refund"
                          ? "The latest successful subscription payment must cover this amount."
                          : adjustmentType === "credit"
                            ? "This lowers a future subscription invoice; it does not send cash back."
                            : "The current monthly price is converted into account credit."
                      }
                    >
                      <input
                        name="value"
                        type="number"
                        min={adjustmentType === "complimentary_months" ? 1 : 0.01}
                        max={adjustmentType === "complimentary_months" ? 24 : 1_000_000}
                        step={adjustmentType === "complimentary_months" ? 1 : 0.01}
                        required
                      />
                    </Field>
                    <Field label="Reason for the audit log">
                      <textarea name="reason" minLength={10} required />
                    </Field>
                    <div className="full">
                      <FormActions
                        submitting={working}
                        disabled={!sensitive.unlocked}
                        submitLabel="Apply billing action"
                      />
                    </div>
                  </form>
                  <div>
                    <h3>Billing action history</h3>
                    {!sensitive.unlocked ? (
                      <Notice tone="warning">
                        Confirm your identity to load protected billing history.
                      </Notice>
                    ) : adjustments.isLoading ? (
                      <SkeletonTable bare columns={6} rows={3} />
                    ) : adjustments.error ? (
                      <ErrorPanel error={adjustments.error} />
                    ) : adjustments.data?.items.length ? (
                      <DataTable
                        caption={`Billing actions for ${selectedTenant.displayName}`}
                        items={adjustments.data.items}
                        getKey={(item) => item.id}
                        columns={[
                          { label: "Action", render: (item) => titleCase(item.type) },
                          {
                            label: "Value",
                            render: (item) =>
                              item.months
                                ? `${item.months} month${item.months === 1 ? "" : "s"}`
                                : formatMoney(item.amountMinor ?? 0, selectedTenant.currency),
                          },
                          {
                            label: "Status",
                            render: (item) => <StatusPill value={item.status} />,
                          },
                          { label: "Attempts", render: (item) => item.attempts },
                          { label: "Created", render: (item) => formatDate(item.createdAt) },
                          {
                            label: "Options",
                            render: (item) =>
                              item.status === "failed" ? (
                                <button
                                  className="button secondary small"
                                  type="button"
                                  disabled={working || !sensitive.unlocked}
                                  onClick={() => retryAdjustment(item)}
                                >
                                  Try again
                                </button>
                              ) : (
                                (item.providerId ?? "Not available")
                              ),
                          },
                        ]}
                      />
                    ) : (
                      <EmptyState
                        title="No billing actions yet"
                        description="Credits, free months, and refunds will appear here with their provider result."
                      />
                    )}
                    {adjustments.data?.items.find((item) => item.status === "failed")?.lastError ? (
                      <Notice tone="danger" title="Provider error">
                        {adjustments.data.items.find((item) => item.status === "failed")?.lastError}
                      </Notice>
                    ) : null}
                  </div>
                </Card>
              ) : null}
              {noteCategories.length && selectedTenant ? (
                <Card>
                  <form className="form-stack" onSubmit={addNote}>
                    <CardHeader
                      title="Internal billing note"
                      description="Platform-only note that is never visible to the customer."
                    />
                    <Field label="Note">
                      <textarea name="body" required minLength={3} />
                    </Field>
                    <input type="hidden" name="category" value="billing" />
                    <FormActions
                      submitting={working}
                      disabled={!sensitive.unlocked}
                      submitLabel="Add billing note"
                    />
                  </form>
                </Card>
              ) : null}
            </>
          ) : null}
          {activeSection.id === "support" ? (
            <>
              <CustomerSelector tenants={items} selected={selectedTenant} onSelect={selectTenant} />
              {selectedTenant ? (
                <div className="content-grid">
                  <Card className="span-5">
                    <CardHeader
                      title={selectedTenant.displayName}
                      description="Only data covered by a live tenant grant can be loaded."
                      actions={<StatusPill value={selectedTenant.status} />}
                    />
                    <button
                      className="button secondary"
                      type="button"
                      disabled={working || !sensitive.unlocked || !canSupport}
                      onClick={loadSnapshot}
                    >
                      Load authorized support snapshot
                    </button>
                    {snapshot ? (
                      <Notice tone="info" title="Tenant-authorized snapshot">
                        <div className="stack">
                          <span>
                            Grant expires {new Date(snapshot.grant.expiresAt).toLocaleString()}.
                          </span>
                          <StatusBreakdown label="Booking states" values={snapshot.bookingStatus} />
                          <StatusBreakdown label="Invoice states" values={snapshot.invoiceStatus} />
                          <span>
                            Recent audit entries:{" "}
                            {snapshot.recentAuditCount ??
                              "not included in the active support grant"}
                            .
                          </span>
                        </div>
                      </Notice>
                    ) : null}
                  </Card>
                  {noteCategories.length ? (
                    <Card className="span-7">
                      <form className="form-stack" onSubmit={addNote}>
                        <CardHeader
                          title="Internal operational note"
                          description="Platform-only note that is never visible to the customer."
                        />
                        <Field label="Category">
                          <select name="category" defaultValue={noteCategories[0]}>
                            {noteCategories.map((category) => (
                              <option key={category} value={category}>
                                {titleCase(category)}
                              </option>
                            ))}
                          </select>
                        </Field>
                        <Field label="Note">
                          <textarea name="body" required minLength={3} />
                        </Field>
                        <FormActions
                          submitting={working}
                          disabled={!sensitive.unlocked}
                          submitLabel="Add internal note"
                        />
                      </form>
                    </Card>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : null}
          {activeSection.id === "reliability" ? (
            <ControlPlane
              tenants={items}
              role={platformRole}
              sensitive={sensitive}
              section="reliability"
              tenantId={selectedTenant?.id ?? ""}
              onTenantChange={selectTenant}
            />
          ) : null}
          {activeSection.id === "notices" ? (
            <ControlPlane
              tenants={items}
              role={platformRole}
              sensitive={sensitive}
              section="notices"
              tenantId={selectedTenant?.id ?? ""}
              onTenantChange={selectTenant}
            />
          ) : null}
          {activeSection.id === "ownership" ? (
            <OwnershipQueue canManage={canManageTenants} sensitive={sensitive} />
          ) : null}
        </>
      ) : null}
    </OperatorFrame>
  );
}
